/**
 * imageCrop.js — square canvas crop with zoom + drag pan (V1.9.33).
 *
 * cropToSquareBlob(imgSrc, { size, scale, dx, dy }) → Promise<Blob>
 *   - imgSrc: object URL or dataURL of the source image
 *   - size:   output square dimension in px (default 512)
 *   - scale:  zoom multiplier applied to the source before cropping (1..3)
 *   - dx,dy:  pan offsets in the source image's own coordinate space (px)
 *
 * The output is a PNG blob whose center is the visible viewport after
 * zoom+pan. Meant to be piped into a multipart/form-data upload.
 */

export function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

/**
 * Given raw source image + zoom+pan, compute the square crop box in
 * source-image coordinates, then draw it into a size×size canvas.
 */
export async function cropToSquareBlob(imgSrc, { size = 512, scale = 1, dx = 0, dy = 0 } = {}) {
    const img = await loadImage(imgSrc);
    // Base square = the smaller dimension of the source. Zoom shrinks it.
    const baseSquare = Math.min(img.naturalWidth, img.naturalHeight);
    const cropSize = baseSquare / Math.max(1, scale);
    // Center of source, then apply user pan (in source px).
    const cx = img.naturalWidth / 2 + dx;
    const cy = img.naturalHeight / 2 + dy;
    // Clamp so the crop rectangle stays inside the source image bounds.
    const sx = Math.max(0, Math.min(img.naturalWidth - cropSize, cx - cropSize / 2));
    const sy = Math.max(0, Math.min(img.naturalHeight - cropSize, cy - cropSize / 2));

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, size, size);
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.92));
}
